import React from 'react';
import Head from 'next/head';
import { NextRouter } from 'next/router';
import { useRouter } from 'next/compat/router';

import useUI from 'hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import PublicEnv from 'shared/utils/public-env';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';

import useStoreFrontOrigin from './use-store-front-origin';

const getDispensaryPathEquivalent = (path: string): string => {
  const splitPath = path.split('/');
  splitPath.splice(1, 1, `dispensary`);

  return splitPath.join('/');
};

type SanitizePathForSeo = {
  sanitizedPath: string;
};

/**
 * Validates that a theme-supplied canonical value is a well-formed, absolute http(s) URL.
 *
 * This is the safety gate for the single-canonical-owner design: an unpatched merchant theme
 * (or one following the broken docs.dutchie.com sample, which produced values like
 * `/stores/txog-fort-worth/stores/txog-fort-worth/product/x`) must never become the sole
 * canonical on the page. If validation fails for any reason — relative path, empty/whitespace
 * string, non-http(s) scheme, or an unparseable string — the caller falls back to the
 * core-computed canonical instead of trusting the theme.
 */
function isValidAbsoluteHttpUrl(value: string | null | undefined): value is string {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function warnInvalidThemeCanonical(value: string): void {
  // Dev-only signal for theme developers: tells them why their canonical isn't taking effect,
  // without spamming production logs/consoles for every page view on an unpatched theme.
  if (process.env.NODE_ENV !== 'production') {
    console.warn(
      `[CanonicalTag] Ignoring theme-supplied metaFields.canonical because it is not a well-formed absolute http(s) URL: ${JSON.stringify(
        value
      )}. Expected an absolute URL, e.g. "https://example.com/stores/my-store/product/some-slug". Falling back to the core-computed canonical.`
    );
  }
}

function sanitizePathForSeo({ asPath }: Partial<NextRouter>): SanitizePathForSeo {
  let sanitizedPath = asPath ?? '';

  /*
    We need to sanitized the path and remove the query params.
    For seo purposes they are unecessarily creating duplicate indices.
  */
  if (asPath) {
    const [path, queryString] = asPath.split('?');
    sanitizedPath = path;

    /*
      Exception: `sortby=relevance` is auto-applied client-side by
      useSetUpRelevanceAsInitialSort as the category's default sort, so a bare path
      immediately gets this query param pushed onto it again. Stripping it here made the
      canonical tag point back to a URL the page redirects away from, creating a
      canonical/redirect loop that kept search engines from indexing either URL.
    */
    const sortby = queryString ? new URLSearchParams(queryString).get('sortby') : null;
    if (sortby === 'relevance') {
      sanitizedPath = `${path}?sortby=relevance`;
    }
  }
  return { sanitizedPath };
}

export default function CanonicalTag(): JSX.Element | null {
  const router = useRouter();
  const UI = useUI();
  const { dispensary } = useDispensary();
  const storeFrontOrigin = useStoreFrontOrigin();
  const { metaFields } = useCommerceComponents();

  const getCanonicalUrl = (): string => {
    const themeCanonical = metaFields?.canonical;
    if (isValidAbsoluteHttpUrl(themeCanonical)) {
      return themeCanonical;
    }
    if (typeof themeCanonical === 'string') {
      warnInvalidThemeCanonical(themeCanonical);
    }

    const hasCustomDomain = !!dispensary?.customDomainSettings?.domain;
    const { sanitizedPath } = sanitizePathForSeo(router ?? {});

    /*
      If viewing storeFront variant (regardless of domain it's served from), prefer dispensary's custom
      domain as canonical, if it exists. In all other cases (dutchie.com, no custom domain exists, etc.),
      default to marketplace URL from env.
    */
    if (UI.isVariant(`store-front`) && hasCustomDomain) {
      return `${storeFrontOrigin}${sanitizedPath}`;
    }

    // For SEO, we want /stores, /embedded-menu, and /kiosks to link back to their /dispensary equivalents
    if (UI.isVariant(`store-front`) || UI.isVariant(`embedded`) || UI.isVariant(`kiosk`)) {
      return `${PublicEnv.consumerUrl}${getDispensaryPathEquivalent(sanitizedPath)}`;
    }

    // All other cases, we self-reference
    return `${PublicEnv.consumerUrl}${sanitizedPath}`;
  };

  return (
    <Head>
      <link rel='canonical' href={getCanonicalUrl()} />
    </Head>
  );
}
